/* Shared product-page content. All specifications and images come from section data. */
(() => {
  const normalize = (value) => String(value ?? '').trim().toLowerCase();
  const hasValue = (value) => value !== null && value !== undefined && String(value).trim() !== '';
  const matches = (rule, state) => (!rule.variantId || String(rule.variantId) === String(state.variant?.id))
    && (!rule.size || normalize(rule.size) === normalize(state.size))
    && (!rule.color || normalize(rule.color) === normalize(state.color));
  const rank = (rule) => (rule.variantId ? 8 : 0) + (rule.size ? 2 : 0) + (rule.color ? 1 : 0);
  const matching = (rules, state) => rules.filter((rule) => matches(rule, state)).sort((a, b) => rank(a) - rank(b));
  const specsFor = (rules, state) => matching(rules, state).reduce((result, rule) => {
    Object.entries(rule.specs || {}).forEach(([key, value]) => { if (hasValue(value)) result[key] = value; });
    if (hasValue(rule.copy)) result.copy = rule.copy;
    return result;
  }, {});
  const contentFor = (rules, key, state, fallbackState) => {
    const candidates = rules.filter((rule) => rule.key === key);
    // Fallback applies to images only. Never borrow another size's text/specifications.
    const current = matching(candidates, state);
    const fallback = matching(candidates, fallbackState);
    const result = {};
    for (const rule of [...fallback, ...current]) if (rule.image?.src) result.image = rule.image;
    for (const rule of current) for (const field of ['heading', 'text']) {
      if (hasValue(rule[field])) result[field] = rule[field];
    }
    return result;
  };

  // Pure resolvers are also used by the regression tests.
  if (typeof module !== 'undefined' && module.exports) {
    module.exports = { matches, specsFor, contentFor };
    return;
  }
  if (window.XCWVariantContent) { window.XCWVariantContent.init(); return; }
  const controllers = new Map();
  class VariantContent {
    constructor(node, config) {
      this.node = node;
      this.config = config;
      this.abort = new AbortController();
      this.originals = new WeakMap();
      this.sizeIndex = config.optionNames.findIndex((name) => config.sizeOption
        ? normalize(name) === normalize(config.sizeOption) : /size|span|diameter|blade length/i.test(name));
      this.colorIndex = config.optionNames.findIndex((name) => config.colorOption
        ? normalize(name) === normalize(config.colorOption) : /colou?r|finish/i.test(name));
      this.variants = new Map(config.variants.map((variant) => [String(variant.id), variant]));
      this.defaultVariant = this.variants.get(String(config.defaultVariantId)) || config.variants[0];
      const options = { signal: this.abort.signal };
      document.addEventListener('variant:update', (event) => {
        if (String(event.detail?.data?.productId) !== String(config.productId)) return;
        if (!event.target.closest?.('.product-information')) return;
        this.render(this.readVariant());
      }, options);
      document.addEventListener('change', (event) => {
        if (event.target.closest?.('variant-picker') !== this.picker()) return;
        // Horizon resolves dependent color/size options synchronously first.
        queueMicrotask(() => this.render(this.readVariant()));
      }, options);
      window.addEventListener('popstate', () => {
        const id = new URL(location.href).searchParams.get('variant') || String(config.initialVariantId);
        if (id && this.variants.has(id)) this.select(id);
        else this.render(this.readVariant());
      }, options);
      this.render(this.readVariant());
    }
    picker() {
      return [...document.querySelectorAll('.product-information variant-picker')]
        .find((picker) => picker.dataset.productId === String(this.config.productId));
    }
    readVariant() {
      const picker = this.picker();
      if (picker) {
        const values = this.config.optionNames.map((_, index) => {
          const group = picker.querySelector(`[data-option-position="${index + 1}"]`);
          return group?.querySelector('input:checked, select')?.value;
        });
        if (values.every(hasValue)) return this.config.variants.find((variant) =>
          variant.options.every((value, index) => value === values[index])) || null;
      }
      return this.variants.get(String(this.config.initialVariantId)) || null;
    }
    stateFor(variant) {
      return { productId: String(this.config.productId), variant,
        size: variant?.options[this.sizeIndex] || '', color: variant?.options[this.colorIndex] || '' };
    }
    profile(variant) { return specsFor(this.config.specProfiles, this.stateFor(variant)); }
    remember(node) {
      if (!this.originals.has(node)) this.originals.set(node, {
        html: node.innerHTML, hidden: node.hidden,
        src: node.getAttribute('src'), srcset: node.getAttribute('srcset'), sizes: node.getAttribute('sizes'),
        alt: node.getAttribute('alt'), width: node.getAttribute('width'), height: node.getAttribute('height'),
      });
      return this.originals.get(node);
    }
    image(node, data) {
      const original = this.remember(node);
      node.hidden = data?.src ? false : original.hidden;
      for (const key of ['src', 'srcset', 'sizes', 'alt', 'width', 'height']) {
        const value = data ? (data[key] ?? (key === 'sizes' ? original.sizes : null)) : original[key];
        if (value === null || value === undefined) node.removeAttribute(key);
        else if (node.getAttribute(key) !== String(value)) node.setAttribute(key, value);
      }
    }
    render(variant) {
      if (!this.node.isConnected) return;
      const state = this.stateFor(variant);
      const specs = variant ? this.profile(variant) : {};
      if (state.size) specs.blade_span = state.size.replace(/(?:''|"|”|″)$/, '″');
      const scope = document.getElementById('MainContent');
      if (!scope) return;
      scope.querySelectorAll('[data-vc-spec]').forEach((cell) => {
        const original = this.remember(cell);
        const value = specs[cell.dataset.vcSpec];
        if (hasValue(value)) cell.textContent = value;
        else if (cell.innerHTML !== original.html) cell.innerHTML = original.html;
      });
      scope.querySelectorAll('[data-vc-copy]').forEach((node) => {
        node.textContent = specs.copy || '';
        node.hidden = !specs.copy;
      });
      scope.querySelectorAll('[data-vc-current]').forEach((node) => {
        node.textContent = variant ? `Selected: ${variant.options.map((v, i) => i === this.sizeIndex ? v.replace(/(?:''|"|”|″)$/, '″') : v).join(' / ')}` : 'Choose an available combination';
      });
      const fallback = this.stateFor(this.defaultVariant);
      scope.querySelectorAll('[data-vc-slot]').forEach((slot) => {
        const content = variant ? contentFor(this.config.content, slot.dataset.vcSlot, state, fallback) : {};
        slot.querySelectorAll('[data-vc-image]').forEach((node) => this.image(node, content.image));
        slot.querySelectorAll('[data-vc-placeholder]').forEach((node) => {
          const original = this.remember(node);
          node.hidden = content.image?.src ? true : original.hidden;
        });
        for (const field of ['heading', 'text']) slot.querySelectorAll(`[data-vc-${field}]`).forEach((node) => {
          const original = this.remember(node);
          if (hasValue(content[field])) { node.textContent = content[field]; node.hidden = false; }
          else { node.innerHTML = original.html; node.hidden = original.hidden; }
        });
      });
      this.state = { ...state, specs };
      document.dispatchEvent(new CustomEvent('xcw:content-update', { detail: this.state }));
    }
    select(id) {
      const variant = this.variants.get(String(id));
      const picker = this.picker();
      if (!variant || !picker) return false;
      const controls = variant.options.map((value, index) => {
        const group = picker.querySelector(`[data-option-position="${index + 1}"]`);
        return [...(group?.querySelectorAll('input') || [])].find((input) => input.value === value)
          || group?.querySelector('select');
      });
      if (controls.some((control) => !control) || typeof picker.updateSelectedOption !== 'function') return false;
      controls.forEach((control, index) => {
        if (control.tagName === 'SELECT') control.value = variant.options[index];
        else control.checked = true;
        picker.updateSelectedOption(control);
      });
      controls.at(-1).dispatchEvent(new Event('change', { bubbles: true }));
      return true;
    }
    destroy() { this.abort.abort(); }
  }
  function init() {
    for (const [id, controller] of controllers) if (!controller.node.isConnected) {
      controller.destroy(); controllers.delete(id);
    }
    document.querySelectorAll('[data-vc-config]').forEach((node) => {
      let config;
      try { config = JSON.parse(node.textContent); } catch { return; }
      const id = String(config.productId);
      if (controllers.get(id)?.node === node) return;
      controllers.get(id)?.destroy();
      if (!Array.isArray(config.variants) || !Array.isArray(config.optionNames)) return;
      const controller = new VariantContent(node, config);
      controllers.set(id, controller);
      // Publish again after registration so dependent widgets can resolve the controller.
      controller.render(controller.readVariant());
    });
  }
  window.XCWVariantContent = { init, get: (id) => controllers.get(String(id)) };
  document.addEventListener('shopify:section:load', () => {
    init(); controllers.forEach((controller) => controller.render(controller.readVariant()));
  });
  document.addEventListener('shopify:section:unload', (event) => {
    for (const [id, controller] of controllers) if (event.target.contains(controller.node)) {
      controller.destroy(); controllers.delete(id);
    }
  });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
